import { useState } from "react";
import { NavLink, useNavigate, Outlet } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  SquaresFour, MagicWand, MagnifyingGlass, BookmarkSimple, FloppyDisk, Briefcase,
  Files, ChatText, UserCircle, Gear, SignOut, List, X, Buildings, UsersThree, Compass,
} from "@phosphor-icons/react";
import ProfileSwitcher from "@/components/ProfileSwitcher";
import SupportWidget from "@/components/SupportWidget";

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: SquaresFour },
  { to: "/career-fit", label: "Career fit", icon: Compass },
  { to: "/matches", label: "Your matches", icon: MagicWand },
  { to: "/jobs", label: "Find jobs", icon: MagnifyingGlass },
  { to: "/saved", label: "Saved jobs", icon: BookmarkSimple },
  { to: "/searches", label: "Saved searches", icon: FloppyDisk },
  { to: "/applications", label: "Applications", icon: Briefcase },
  { to: "/documents", label: "Documents", icon: Files },
  { to: "/interview", label: "Pre-interview", icon: ChatText },
  { to: "/profile", label: "Profile", icon: UserCircle },
  { to: "/settings", label: "Settings", icon: Gear },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const role = user?.role;
  let nav;
  if (role === "employer") {
    nav = [{ to: "/employer", label: "Workspace", icon: Buildings }, { to: "/plans", label: "Plans", icon: Compass }, { to: "/settings", label: "Settings", icon: Gear }];
  } else if (role === "recruiter") {
    nav = [{ to: "/recruiter", label: "Workspace", icon: UsersThree }, { to: "/recruiter/clients", label: "Clients & Pools", icon: Buildings }, { to: "/plans", label: "Plans", icon: Compass }, { to: "/settings", label: "Settings", icon: Gear }];
  } else {
    nav = role === "admin" ? [...NAV, { to: "/admin", label: "Admin", icon: Gear }] : NAV;
  }

  const doLogout = async () => {
    await logout();
    navigate("/login");
  };

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="px-6 py-5 border-b border-slate-200">
        <NavLink to="/dashboard" className="flex items-center gap-2" data-testid="sidebar-logo">
          <span className="h-6 w-6 rounded-sm bg-slate-900 flex items-center justify-center text-white font-heading font-black text-xs">E</span>
          <span className="font-heading font-bold text-slate-900 tracking-tight">Eligvia</span>
        </NavLink>
      </div>
      <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
        {nav.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-${item.label.toLowerCase().replace(/ /g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                }`
              }
            >
              <Icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              {item.label}
            </NavLink>
          );
        })}
      </nav>
      <div className="border-t border-slate-200 p-3">
        {(role === "candidate" || role === "admin") && <div className="mb-3"><ProfileSwitcher /></div>}
        <div className="flex items-center gap-3 px-2 py-2">
          <div className="h-8 w-8 rounded-full bg-slate-200 flex items-center justify-center text-slate-700 font-semibold text-sm overflow-hidden">
            {user?.picture ? <img src={user.picture} alt="" className="h-full w-full object-cover" /> : (user?.name?.[0] || "U")}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-slate-900 truncate">{user?.name || "You"}</p>
            <p className="text-xs text-slate-500 truncate capitalize">{user?.role || "Member"} · {user?.email}</p>
          </div>
        </div>
        <button
          onClick={doLogout}
          data-testid="logout-btn"
          className="mt-1 flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
        >
          <SignOut style={{ width: 18, height: 18 }} /> Sign out
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:block fixed inset-y-0 left-0 w-64 bg-white border-r border-slate-200 z-30">
        {sidebar}
      </aside>

      {/* Mobile top bar */}
      <div className="lg:hidden sticky top-0 z-40 flex items-center justify-between bg-white border-b border-slate-200 px-4 py-3">
        <div className="flex items-center gap-2">
          <span className="h-6 w-6 rounded-sm bg-slate-900 flex items-center justify-center text-white font-heading font-black text-xs">E</span>
          <span className="font-heading font-bold text-slate-900">Eligvia</span>
        </div>
        <button onClick={() => setOpen(true)} data-testid="mobile-menu-btn" className="p-2 text-slate-700">
          <List size={22} />
        </button>
      </div>

      {open && (
        <div className="lg:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-slate-900/40" onClick={() => setOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64 bg-white shadow-xl">
            <button onClick={() => setOpen(false)} className="absolute right-3 top-4 p-1 text-slate-500 z-10"><X size={20} /></button>
            {sidebar}
          </div>
        </div>
      )}

      <main className="lg:pl-64">
        <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
          <Outlet />
        </div>
      </main>
      <SupportWidget />
    </div>
  );
}
